export const RoleType = {
  ConsumerWebsite: "ConsumerWebsite",
  RestaurantWebsite: "RestaurantWebsite",
  DeliveryOperationsPortal: "DeliveryOperationsPortal",
  LocalAreaManager: "LocalAreaManager",
  RestaurantManager: "RestaurantManager",
} as const;

export type RoleType = (typeof RoleType)[keyof typeof RoleType];

export const PermissionLevel = {
  Admin: "Admin",
  Owner: "Owner",
  RegionalAreaManager: "RegionalAreaManager",
  Manager: "Manager",
  LocalAreaManager: "LocalAreaManager",
  Executive: "Executive",
  Sales: "Sales",
  Accounting: "Accounting",
  Supervisor: "Supervisor",
  Operator: "Operator",
  Driver: "Driver",
  ReadWriteUser: "ReadWriteUser",
  ReadOnlyUser: "ReadOnlyUser",
} as const;

export type PermissionLevel =
  (typeof PermissionLevel)[keyof typeof PermissionLevel];

export const PERMISSION_HIERARCHY: Record<PermissionLevel, number> = {
  Admin: 0,
  Owner: 30,
  RegionalAreaManager: 40,
  Manager: 50,
  LocalAreaManager: 50,
  Executive: 60,
  Sales: 60,
  Accounting: 60,
  Supervisor: 60,
  Operator: 50,
  Driver: 60,
  ReadWriteUser: 85,
  ReadOnlyUser: 90,
};

export interface RolePermission {
  permission: PermissionLevel;
  data?: {
    business_id?: number;
    business_ids?: number[];
    restaurant_id?: number;
    restaurant_ids?: number[];
    profile?: number;
    profiles?: number[];
  };
}

export interface PermissionsResponse {
  user_id: number;
  roles: Record<string, RolePermission>;
}

export const ROLE_DISPLAY_NAMES: Record<RoleType, string> = {
  ConsumerWebsite: "Consumer",
  RestaurantWebsite: "Restaurant Website",
  DeliveryOperationsPortal: "Delivery Operations",
  LocalAreaManager: "Local Area Manager",
  RestaurantManager: "Restaurant Manager",
};

export const PERMISSION_DISPLAY_NAMES: Record<PermissionLevel, string> = {
  Admin: "Administrator",
  Owner: "Owner",
  RegionalAreaManager: "Regional Area Manager",
  Manager: "Manager",
  LocalAreaManager: "Local Area Manager",
  Executive: "Executive",
  Sales: "Sales",
  Accounting: "Accounting",
  Supervisor: "Supervisor",
  Operator: "Operator",
  Driver: "Driver",
  ReadWriteUser: "Read/Write User",
  ReadOnlyUser: "Read Only User",
};

export function getPermissionLevel(level: PermissionLevel): number {
  return PERMISSION_HIERARCHY[level] ?? 100;
}

export function hasMinimumPermission(
  userLevel: PermissionLevel,
  requiredLevel: PermissionLevel
): boolean {
  return getPermissionLevel(userLevel) <= getPermissionLevel(requiredLevel);
}

export function isAdmin(permission: PermissionLevel): boolean {
  return permission === PermissionLevel.Admin;
}

export function hasResourceAccess(
  rolePermission: RolePermission,
  resourceType: "business" | "restaurant" | "profile",
  resourceId: number
): boolean {
  if (isAdmin(rolePermission.permission)) return true;
  if (!rolePermission.data) return false;

  switch (resourceType) {
    case "business":
      return (
        rolePermission.data.business_id === resourceId ||
        rolePermission.data.business_ids?.includes(resourceId) === true
      );
    case "restaurant":
      return (
        rolePermission.data.restaurant_id === resourceId ||
        rolePermission.data.restaurant_ids?.includes(resourceId) === true
      );
    case "profile":
      return (
        rolePermission.data.profile === resourceId ||
        rolePermission.data.profiles?.includes(resourceId) === true
      );
    default:
      return false;
  }
}
